import { IonIcon } from "@ionic/react";
import { useTranslation } from "react-i18next";
import {
  calendarOutline,
  chevronForwardOutline,
  closeOutline,
  notificationsOutline,
  personOutline,
} from "ionicons/icons";
import { buildMissingFieldLabels } from "@/components/modal/app/profile-nudge";
import { usePreferredHaptics } from "@/lib/hooks/use-preferred-haptics";
import { type SetupStep } from "@/lib/hooks/use-setup-nudges";
import { Routes } from "@/lib/router";
import { history } from "@/lib/router/history";

const STEPS: Record<SetupStep, { icon: string; route: () => string }> = {
  profile: { icon: personOutline, route: () => Routes.PersonalInformation() },
  notifications: { icon: notificationsOutline, route: () => Routes.Notifications() },
  calendar: { icon: calendarOutline, route: () => Routes.Calendar() },
};

/**
 * De kaart op Vandaag die vraagt om meldingen en om een gekoppelde agenda.
 *
 * Twee dingen die je één keer instelt en die daarna de app pas echt laten werken -- en die bijna
 * niemand uit zichzelf doet, omdat er geen moment is waarop erom gevraagd wordt.
 *
 * Waarom een kaart en geen venster bij het opstarten: dat is het patroon dat mensen wegtikken
 * zonder te lezen, en dan is de vraag voorgoed verbruikt. Deze staat er, wacht, en je kunt hem
 * wegtikken -- waarna hij over twee weken terugkomt. Bovendien vraagt hij niet zelf om toestemming:
 * hij brengt je naar het scherm waar staat wat je aanzet en waarom, en daar geef je hem.
 * Toestemming vragen op het moment dat iemand snapt waarvoor is de hele les van iOS.
 *
 * Een stap die af is verdwijnt vanzelf, en met de laatste stap verdwijnt de kaart. Niets om af te
 * vinken, niets om op te ruimen.
 */
export function SetupNudge({
  steps,
  missingFields,
  onDismiss,
}: {
  steps: SetupStep[];
  missingFields: string[];
  onDismiss: () => void;
}) {
  const { t } = useTranslation();
  const PreferredHaptics = usePreferredHaptics();

  if (steps.length === 0) {
    return null;
  }

  // Altijd één, ook als er vijf dingen missen. Een lijstje van vijf is geen zetje meer maar een
  // rekening; één regel is iets wat je even doet, en de volgende komt vanzelf als deze af is.
  //
  // Er komt niet altijd een naam uit: voor een eigen veld van de organisatie levert
  // `buildMissingFieldLabels` bewust niets zolang de definities niet geladen zijn, en die laden we
  // hier niet. Dan noemen we het veld dus niet.
  const [missingLabel] = buildMissingFieldLabels(missingFields, undefined, t);

  const hintFor = (step: SetupStep) => {
    if (step !== "profile") {
      return t(`routes:today.setup.${step}.hint`);
    }

    // Zonder naam een aparte regel, en niet dezelfde zin zonder argument: die zin heeft een
    // `{field}` in zich, en ICU laat zo'n plaatshouder dan letterlijk staan. Op het scherm stond
    // "{field} is still missing." -- een lek in de machinerie waar een mededeling hoort.
    return missingLabel
      ? t("routes:today.setup.profile.hint", { field: missingLabel })
      : t("routes:today.setup.profile.hint-generic");
  };

  const open = (step: SetupStep) => {
    void PreferredHaptics.impact();
    history.push(STEPS[step].route());
  };

  return (
    <section className="flex flex-col gap-3 rounded-2xl border border-solid border-bordercolor-primary bg-surface-primary p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <h2 className="text-sm font-bold text-txt-primary">{t("routes:today.setup.title")}</h2>
          <p className="text-xs text-txt-tertiary">{t("routes:today.setup.hint")}</p>
        </div>

        <button
          type="button"
          onClick={() => {
            void PreferredHaptics.impact();
            onDismiss();
          }}
          aria-label={t("routes:today.setup.dismiss")}
          // 44 punten, ook al is het kruisje kleiner: een sluitknop die je moet mikken is erger
          // dan geen sluitknop.
          className="-m-2 flex size-11 shrink-0 items-center justify-center rounded-full active:opacity-70"
        >
          <IonIcon icon={closeOutline} className="size-5 text-txt-tertiary" />
        </button>
      </div>

      <ul className="flex flex-col">
        {steps.map((step) => (
          <li key={step}>
            <button
              type="button"
              onClick={() => open(step)}
              className="flex min-h-[44px] w-full items-center gap-3 py-1 text-left active:opacity-70"
            >
              <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-surface-secondary">
                <IonIcon icon={STEPS[step].icon} className="size-5 text-txt-brand" />
              </span>

              <span className="flex min-w-0 flex-1 flex-col">
                <span className="text-sm font-semibold text-txt-primary">
                  {t(`routes:today.setup.${step}.title`)}
                </span>
                <span className="text-xs text-txt-tertiary">{hintFor(step)}</span>
              </span>

              <IonIcon
                icon={chevronForwardOutline}
                className="size-4 shrink-0 text-txt-tertiary"
                aria-hidden="true"
              />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
